import { useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/hooks/useAuth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Eye, EyeOff } from "lucide-react";

export function Login() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const from = (location.state as { from?: { pathname: string } })?.from?.pathname ?? "/";

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email || !password) {
      toast.error("Ingresa email y contraseña");
      return;
    }
    setLoading(true);
    try {
      await login(email, password);
      toast.success("Sesión iniciada");
      navigate(from, { replace: true });
    } catch (err) {
      toast.error("Error al iniciar sesión. Revisa email y contraseña.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex min-h-screen">
      <div
        className="relative hidden w-1/2 flex-col justify-between overflow-hidden p-12 lg:flex"
        style={{ backgroundColor: "#1F2240" }}
      >
        <div
          className="pointer-events-none absolute inset-0"
          style={{
            backgroundImage:
              "linear-gradient(rgba(255,255,255,0.03) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.03) 1px, transparent 1px)",
            backgroundSize: "60px 60px",
          }}
        />
        <div
          className="pointer-events-none absolute left-0 top-0 h-80 w-80 -translate-x-1/2 -translate-y-1/2 rounded-full opacity-20 blur-3xl"
          style={{ backgroundColor: "#D32F2F" }}
        />
        <div className="relative z-10">
          <img
            src="/logo.png"
            alt="Oriental Ramírez"
            className="mb-16 h-12 w-auto brightness-0 invert"
          />
          <div className="mb-4 h-1 w-16 rounded-full" style={{ backgroundColor: "#D32F2F" }} />
          <h1 className="font-[DM_Sans] text-4xl font-bold leading-tight tracking-tight text-white">
            Panel de
            <br />
            Administración
          </h1>
          <p className="mt-6 max-w-sm text-base leading-relaxed text-white/50">
            Gestiona inventario de vehículos, repuestos, cotizaciones y financiamiento desde un solo lugar.
          </p>
        </div>
        <p className="relative z-10 text-sm text-white/30">
          &copy; {new Date().getFullYear()} Oriental Ramírez. Todos los derechos reservados.
        </p>
      </div>

      <div className="flex w-full flex-col items-center justify-center bg-white px-6 lg:w-1/2">
        <div className="w-full max-w-md">
          <div className="mb-10 lg:hidden">
            <img
              src="/logo.png"
              alt="Oriental Ramírez"
              className="mb-8 h-10 w-auto"
            />
          </div>

          <h2 className="text-3xl font-bold tracking-tight text-gray-900">
            Iniciar sesión
          </h2>
          <p className="mt-2 text-sm text-gray-500">
            Acceso administrativo
          </p>

          <form onSubmit={handleSubmit} className="mt-10 space-y-6">
            <div className="space-y-2">
              <Label
                htmlFor="email"
                className="text-xs font-semibold uppercase tracking-widest text-gray-500"
              >
                Correo electrónico
              </Label>
              <Input
                id="email"
                type="email"
                placeholder="admin@ejemplo.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                autoComplete="email"
                className="h-12 rounded-xl border-gray-200 bg-gray-50 text-gray-900 placeholder:text-gray-400 focus:border-[#D32F2F] focus:ring-[#D32F2F]"
              />
            </div>

            <div className="space-y-2">
              <Label
                htmlFor="password"
                className="text-xs font-semibold uppercase tracking-widest text-gray-500"
              >
                Contraseña
              </Label>
              <div className="relative">
                <Input
                  id="password"
                  type={showPassword ? "text" : "password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  autoComplete="current-password"
                  className="h-12 rounded-xl border-gray-200 bg-gray-50 pr-12 text-gray-900 placeholder:text-gray-400 focus:border-[#D32F2F] focus:ring-[#D32F2F]"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword((prev) => !prev)}
                  className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 transition-colors hover:text-gray-600"
                  tabIndex={-1}
                >
                  {showPassword ? (
                    <EyeOff className="h-5 w-5" />
                  ) : (
                    <Eye className="h-5 w-5" />
                  )}
                </button>
              </div>
            </div>

            <Button
              type="submit"
              className="h-12 w-full rounded-xl text-sm font-semibold text-white"
              style={{ backgroundColor: "#D32F2F" }}
              disabled={loading}
            >
              {loading ? "Entrando…" : "Entrar"}
            </Button>
          </form>
        </div>
      </div>
    </div>
  );
}
